import { FC, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import moment from "moment";
import { pipeline } from "stream";
import { ListProduct, Pipeline, ProductLabels } from "../../../interfaces/Pipeline";
import { CustomField } from "../../../interfaces/Settings";
import { useSettingsStore } from "../../../stores/SettingsStore";
import useUser from "../../../hooks/User";
import { useCustomeStore } from "../../../stores/CustomeStore";
import { usePipelineStore } from "../../../stores/PipelineStore";
import { currencyFormatter, customNumberFormat, getDateRangeDuration, getDateSummary, isColorDarkFromHex } from "../../../functions/general";
import { ExpandableElement } from "../../General/ExpandableElement";
import axios from "axios";
import useAccessToken from "../../../hooks/AccessToken";
import useSWR from "swr";
import BadgeTooltip from "../../General/BadgeTooltip";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Label } from "../../../interfaces/Label";
import { Log, LogAttachment } from "../../../interfaces/Log";
import { calculateDayDurations } from "../../../functions/calculateDuration";
import { useMeetingStore } from "../../../stores/MeetingsStore";

interface DetailPipelineProps {
    dataPipeline: Pipeline
    setShowDetailPipelineModal: React.Dispatch<React.SetStateAction<boolean>>
}

const ChatDetailPipeline: FC<DetailPipelineProps> = ({ dataPipeline, setShowDetailPipelineModal }) => {
    const API_URL = process.env.REACT_APP_API_URL
    const modalRef = useRef<HTMLDivElement | null>(null);
    const token = useAccessToken();
    const intl = useIntl()
    const [customLead, setCustomLead] = useState({} as any)
    const [customFields, setCustomFields] = useState<CustomField>({})
    const { settings } = useSettingsStore()
    const user = useUser()
    const { companyId, keyCustomfield } = useCustomeStore()
    const [data, setData] = useState<Pipeline>()
    const [log, setLog] = useState<Log[]>([])
    const { selectMultiPipeline } = usePipelineStore()
    const [displayMode, setDisplayMode] = useState<string>('pipeline');
    const { simpleMeetings } = useMeetingStore()

    const fetcher = (url: string) => axios.get(url, {
        headers: {
            'X-Auth-token': token
        }
    }).then(res => res.data.data).catch(error => {
        throw error.response ? error.response.data : error;
    })

    const { data: dataDetailPipeline = {}, error: dataDetailPipelineError, isLoading: isLoadingDataDetailPipeline } = useSWR(dataPipeline && displayMode === 'pipeline' ? `${API_URL}v2/lead/${dataPipeline.id}` : null, fetcher)
    const { data: dataLogPipeline = [], error: dataLogPipelineError, isLoading: isLoadingDataLogPipeline } = useSWR(dataPipeline && displayMode === 'log' ? `${API_URL}lead_logs/?lead_id=${dataPipeline.id}` : null, fetcher)

    useEffect(() => {
        if (!isLoadingDataDetailPipeline && Object.keys(dataDetailPipeline).length > 0) {
            setData(dataDetailPipeline)
        }
    }, [dataDetailPipeline])

    useEffect(() => {
        if (!isLoadingDataLogPipeline && dataLogPipeline.length > 0) {
            var data: Log[] = dataLogPipeline

            if (user.data.company_id === companyId.juraganMaterial) {
                data.forEach((eachData: any, index: number) => {
                    if (index !== 0) {
                        eachData.duration = getDateRangeDuration(data[index].date_created, data[index - 1].date_created);
                    } else {
                        eachData.duration = "-"
                    }
                })
            } else {
                data.forEach((eachData: any, index: number) => {
                    if (index < data.length - 1) {
                        if (user.data.company_id === companyId.esb) {
                            eachData.duration = calculateDayDurations(data[index + 1].date_created, data[index].date_created);
                        } else {
                            eachData.duration = getDateRangeDuration(data[index + 1].date_created, data[index].date_created);
                        }
                    } else {
                        eachData.duration = "-"
                    }
                })
            }

            setLog(data)
        }
    }, [dataLogPipeline])

    useEffect(() => {
        if (data) {
            const customData: any = {
                templateName: {
                    name: 'Database',
                    value: dataPipeline.lead_template_name
                },
                date_created: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.TYPE.DATE_CREATED' }),
                    value: moment(data.date_created).format("DD MMM YYYY HH:mm")
                },
                merchant: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME_TITLE' }, { title: settings.merchant_title }),
                    value: returnMerchantName(data)
                },
                pic: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC' }),
                    value: returnOwnerName(data.owner_name, data.labels)
                },
                total_log: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.TOTAL_LOGS' }),
                    value: data.lead_logs_count && customNumberFormat(data.lead_logs_count)
                },
                progress: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PROGRESS' }),
                    value: returnProgress(data)
                },
                phone: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PHONE' }),
                    value: data.merchant_phone
                },
                age: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.AGE' }),
                    value: data.lead_age
                },
                source: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.SOURCE' }),
                    value: returnSource(data)
                },
                product: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT' }),
                    value: returnProduct(data)
                },
                label: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.LABEL' }),
                    value: returnLabel(data)
                },
                nominal: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL' }) + ` (${user?.data?.companyCurrency})`,
                    value: `${data.nominal && currencyFormatter(data.nominal, user?.data?.companyCurrencyNeedRound, user?.data?.companyCurrency)}`
                },
                product_qty: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.SUM_PRODUCTS' }),
                    value: returnQtyProduct(data)
                },
                creator: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CREATED_BY' }),
                    value: data.creator_name
                },
                start_date: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_STARTED' }),
                    value: moment(data.date_start).format("DD MMM YYYY HH:mm")
                },
                end_date: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_ENDED' }),
                    value: data.date_end ? moment(data.date_end).format("DD MMM YYYY HH:mm") : "-"
                },
                note: {
                    name: (user.data.company_id === companyId.elgisa || user.data.company_id === companyId.looyalIT) ? "Opportunity" : intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOTES' }),
                    value: data.note ? <ExpandableElement text={(data.note).replaceAll('\n', '<br/>')} maxHeight={100} /> : "-"
                },
            }

            var newData = {
                ...customData,
                ...Object.keys(customFields).reduce((acc, key) => {
                    let value;
                    if (customFields[key].type === 'invoice_reminder' && data.custom_fields[key]) {
                        if (JSON.parse(data.custom_fields[key] as string).invoice_url && JSON.parse(data.custom_fields[key] as string).date_due) {
                            value = returnInvoiceReminder(JSON.parse(data.custom_fields[key] as string).invoice_url, JSON.parse(data.custom_fields[key] as string).date_due)
                        } else if (JSON.parse(data.custom_fields[key] as string).invoice_url && !JSON.parse(data.custom_fields[key] as string).date_due) {
                            value = <a href={JSON.parse(data.custom_fields[key] as string).invoice_url} target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: "FORM.ACTION.VIEW_INVOICE" })}</a>
                        } else if (!JSON.parse(data.custom_fields[key] as string).invoice_url && JSON.parse(data.custom_fields[key] as string).date_due) {
                            value = moment(JSON.parse(data.custom_fields[key] as string).date_due).format("DD MMM YYYY")
                        } else value = "-"
                    }
                    else if (customFields[key].type === 'date') {
                        value = data.custom_fields[key] ? moment(data.custom_fields[key]).format("DD MMM YYYY") : "-";
                    } else if (customFields[key].type === 'dateTimeRange') {
                        value = data.custom_fields[key] ? (data.custom_fields[key] as string[]).map((item: any) => moment(item).format("DD MMM YYYY HH:mm")).join(' - ') : '-';
                    } else if (customFields[key].type === 'time') {
                        let result = data.custom_fields[key] ? moment(data.custom_fields[key]).format('HH:mm') : '-'
                        value = result
                    } else if (customFields[key].type === 'image' || customFields[key].type === 'images') {
                        value = data.custom_fields[key] ? returnImage(data.custom_fields[key]) : "-";
                    } else if (customFields[key].type === 'file' || customFields[key].type === 'files') {
                        value = data.custom_fields[key] ? returnFile(data.custom_fields[key]) : "-";
                    } else if (customFields[key].type === 'multiple') {
                        value = data.custom_fields[key] ? returnMultiple(data.custom_fields[key] as string) : "-";
                    } else if (customFields[key].type === 'link') {
                        value = data.custom_fields[key] ? returnLink(data.custom_fields[key] as string) : "-";
                    } else if (customFields[key].type === 'serial') {
                        value = typeof data.custom_fields[key] === 'string' ? data.custom_fields[key] : (data.custom_fields[key] as string[])?.length > 0 ? (data.custom_fields[key] as string[]).join('') : '-';
                    } else if (customFields[key].type === 'merchant' || customFields[key].type === 'user' || customFields[key].type === 'pipeline' || customFields[key].type === 'meeting') {
                        value = returnCustomFieldMaster(data, key, customFields[key].type);
                    } else {
                        value = data.custom_fields[key] ? data.custom_fields[key] : "-";
                    }

                    return {
                        ...acc,
                        [key]: {
                            name: customFields[key].name,
                            value
                        }
                    };
                }, {})
            };

            if (user.data.company_id === companyId.enterpriseSales) {
                newData = {
                    ...newData,
                    [keyCustomfield.serial.enterpriseSales]: {
                        name: 'Invoice',
                        value: data.custom_fields[keyCustomfield.serial.enterpriseSales] ? (data.custom_fields[keyCustomfield.serial.enterpriseSales] as string[]).join('') : "-"
                    }
                }
            }

            if (user.data.company_id === companyId.enterpriseService) {
                newData = {
                    ...newData,
                    [keyCustomfield.serial.enterpriseService]: {
                        name: 'Invoice',
                        value: data.custom_fields[keyCustomfield.serial.enterpriseService] ? (data.custom_fields[keyCustomfield.serial.enterpriseService] as string[]).join('') : "-"
                    }
                }
            }

            setCustomLead(newData);
        }
    }, [customFields, data])

    useEffect(() => {
        if (Object.keys(selectMultiPipeline).length !== 0) {
            setCustomFields(selectMultiPipeline.customFields)
        }
    }, [selectMultiPipeline])

    const returnOwnerName = (ownerName: string, labels: Label[]) => {
        return (
            <div className="d-flex gap-2 w-100">
                <div>{ownerName}</div>
                <OverlayTrigger
                    placement='bottom'
                    overlay={
                        <Tooltip id='tooltip-labels'>
                            {labels?.map((label, index) => (
                                <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'wrap' }} key={index}>
                                    <svg viewBox="0 0 24 24" height="20" width="20" className="cursor-pointer me-2" preserveAspectRatio="xMidYMid meet" fill="none" style={{ color: label.color || "black", width: '20px', flexShrink: 0, marginBottom: "auto" }}>
                                        <path fill="currentColor" fillRule="evenodd" clipRule="evenodd" d="M15.393 5C16.314 5 17.167 5.447 17.685 6.182L21.812 12L21.346 12.657L17.686 17.816C17.166 18.553 16.314 19 15.393 19L5.81 18.992C4.262 18.992 3 17.738 3 16.19V7.81C3 6.261 4.262 5.008 5.809 5.008L15.393 5Z"></path>
                                    </svg>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }}>
                                        {label.name}
                                    </span>
                                </div>
                            ))
                            }
                        </Tooltip>
                    }
                >
                    <span
                        className='cursor-pointer'
                        data-toggle='tooltip'
                        data-placement='bottom'
                        title=''
                    >
                        {(labels?.length && labels.length > 0) ? (
                            labels.length === 1 ? (
                                <svg viewBox="0 0 24 24" height="19" width="20" fill="none" style={{ color: labels[0].color || "black" }} >
                                    <path fill="currentColor" fillRule="evenodd" clipRule="evenodd" d="M15.393 5C16.314 5 17.167 5.447 17.685 6.182L21.812 12L21.346 12.657L17.686 17.816C17.166 18.553 16.314 19 15.393 19L5.81 18.992C4.262 18.992 3 17.738 3 16.19V7.81C3 6.261 4.262 5.008 5.809 5.008L15.393 5Z" />
                                </svg>
                            ) : (
                                <div className="ps-1" style={{ color: labels[1].color || "black" }}>
                                    <svg width="18px" height="15px" viewBox="0 0 18 15">
                                        <path fill={`${labels[0].color}` || "black"} d="M11.208,3.925H2.236C1.556,3.925,1,4.565,1,5.357v7.213C1,13.362,1.556,14,2.236,14h8.972 c0.414,0,0.785-0.237,1.007-0.604l2.701-4.433L12.215,4.53C11.993,4.162,11.622,3.925,11.208,3.925z" />
                                        <path fill="currentColor" d="M15.541,7.832L13.164,3.93 c-0.423-0.698-1.15-1.142-1.956-1.142H2.595V2.432C2.595,1.64,3.151,1,3.831,1h9.1c0.414,0,0.782,0.237,1.005,0.605l2.696,4.433 L15.541,7.832z" />
                                    </svg>
                                </div>

                            )
                        ) : <></>}
                    </span>
                </OverlayTrigger>
            </div>
        )
    }

    const returnMerchantName = (data: Pipeline) => {
        switch (data.hot_warm_cold_status) {
            case 'hot':
                return <><span>{data.merchant_name}</span><span className="badge badge-light-danger ms-3">Hot</span></>
            case 'warm':
                return <><span>{data.merchant_name}</span><span className="badge badge-light-warning ms-3">Warm</span></>
            case 'cold':
                return <><span>{data.merchant_name}</span><span className="badge badge-light-primary ms-3">Cold</span></>
            default:
                return data.merchant_name
        }
    }

    const returnProgress = (data: Pipeline) => {
        const progressName = data.lead_progress_name;
        const subProgressName = data.lead_subprogress_name;
        const progressColor = data.lead_progress_color;

        // Determine the badge style based on progress color
        const badgeStyle = progressColor
            ? { backgroundColor: progressColor, color: isColorDarkFromHex(progressColor) ? "white" : "black" }
            : { backgroundColor: "transparent", }; // Default when no color

        // Generate badge content
        const badgeContent = subProgressName
            ? `${progressName} (${subProgressName})`
            : progressName;

        // Return the badge component
        return (
            <BadgeTooltip tooltipText={badgeContent} badgeClass={`${progressColor ? '' : 'badge-light-info'}`} style={badgeStyle}>
                {badgeContent}
            </BadgeTooltip>
        );
    }

    const returnProduct = (data: Pipeline) => {
        return <div dangerouslySetInnerHTML={{ __html: data.leadProducts?.map((dataProduct: ListProduct) => dataProduct.productName).join(', ') }} />
    }

    const returnLabel = (data: Pipeline) => {
        const uniqueLabels = [...new Set(data.leadProducts.flatMap(product => product.productLabels?.map(label => label.name) || []))];
        return uniqueLabels.length > 0 ? uniqueLabels.join(', ') : "-";
    }

    const returnQtyProduct = (data: Pipeline) => {
        if (data.leadProducts) return customNumberFormat(data.leadProducts?.reduce((total, dataProduct) => total + Number(dataProduct.leadProductQty), 0))
        else return 0
    }

    const returnSource = (data: Pipeline) => {
        switch (data.lead_source_name) {
            case 'INBOUND':
                return <span className="badge badge-light-primary">Inbound</span>
            case 'OUTBOUND':
                return <span className="badge badge-light-success">Outbound</span>
            default:
                return (
                    <BadgeTooltip tooltipText={data.lead_source_name} badgeClass={`badge-light-info`}>
                        {data.lead_source_name}
                    </BadgeTooltip>
                )
        }
    }

    const returnImage = (url: string | string[]) => {
        if (Array.isArray(url)) {
            return url.map((data: string, index: number) => (
                <span key={index}>
                    <a href={data} target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: "FORM.ACTION.VIEW_PICTURE" })} {index + 1}</a>
                    {index < url.length - 1 && ', '}
                </span>
            ));
        } else {
            return (
                <a href={url} target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: "FORM.ACTION.VIEW_PICTURE" })}</a>
            );
        }
    };

    const returnFile = (url: string | string[]) => {
        if (Array.isArray(url)) {
            return url.map((data: string, index: number) => (
                <span key={index}>
                    <a href={data} target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: "FORM.ACTION.VIEW_FILE" })} {index + 1}</a>
                    {index < url.length - 1 && ', '}
                </span>
            ));
        } else {
            return (
                <a href={url} target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: "FORM.ACTION.VIEW_FILE" })}</a>
            );
        }
    };

    const returnMultiple = (data: string) => {
        const values = data && data.toString()
        return values ? values.split(',').join(', ') : '-'
    }

    const returnLink = (data: string) => {
        return (
            <a href={data} rel="noreferrer" className="btn btn-sm btn-outline btn-outline-default btn-active-light text-gray-600" target="_blank">Lihat<i className="fas fa-external-link-alt fs-6 ms-2 p-0"></i></a>
        )
    }

    const returnInvoiceReminder = (url: string, date: string) => {
        return (
            <div>
                {moment(date).format("DD MMM YYYY")}, <a href={url} target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: "FORM.ACTION.VIEW_INVOICE" })}</a>
            </div>
        )
    }

    const returnCustomFieldMaster = (row: Pipeline, key: string, type: string) => {
        const values = row.info_custom_fields && row.info_custom_fields[key];

        if (type === 'pipeline') {
            return (
                (Array.isArray(values) && values.length > 0) ?
                    <>
                        <span><b>{(values[0] as any).name}</b> / Rp.{customNumberFormat((values[0] as any).nominal)} / {moment((values[0] as any).dateCreated).format("DD MMM YYYY")}</span>
                    </>
                    :
                    '-'
            )
        } else if (type === 'merchant') {
            return (
                (Array.isArray(values) && values.length > 0) ?
                    <span>{values.map((item: any) => item.merchant_name).join(', ')}</span>
                    :
                    '-'

            )
        } else if (type === 'user') {
            return (
                (Array.isArray(values) && values.length > 0) ?
                    <span>{values.map((item: any) => item.name).join(', ')}</span>
                    :
                    '-'

            )
        } else if (type === 'meeting') {
            return (
                (Array.isArray(values) && values.length > 0) ?
                    <span>{values.map((item: any) => item.merchant_name + " / " + moment(item.date_meet).format('DD MMM YYYY') + " / " + item.meeting_status).join(', ')}</span>
                    :
                    '-'

            )
        } else return '-'
    }

    const hasValidAttachments = (attachments: LogAttachment[] | undefined): boolean => {
        if (!attachments || attachments.length === 0) {
            return false;
        }

        const isValidUrl = (url: string): boolean => {
            try {
                new URL(url);
                return true;
            } catch {
                return false;
            }
        };

        return attachments.some(attachment => isValidUrl(attachment.url));
    };

    return (
        <div className="card" style={{ minHeight: "calc(100vh - 1rem)" }}>
            <h3 className="py-3 px-5 d-flex align-items-center gap-3">
                <i className="fas fa-times fs-3 text-hover-primary cursor-pointer" onClick={() => setShowDetailPipelineModal(false)}></i>
                Detail {dataPipeline.merchant_name}
            </h3>
            <div className="card-header border-0">
                <div className="card-title pt-3 mb-0 gap-4 gap-lg-10 gap-xl-10 nav nav-tabs border-bottom-0">
                    <div className={`fs-4 fw-bolder pb-3 cursor-pointer ${displayMode === 'pipeline' ? 'border-bottom border-3 border-primary' : 'text-muted'}`} onClick={() => setDisplayMode('pipeline')}>{settings.pipeline_title}</div>
                    <div className={`fs-4 fw-bolder pb-3 cursor-pointer ${displayMode === 'log' ? 'border-bottom border-3 border-primary' : 'text-muted'}`} onClick={() => setDisplayMode('log')}>Log</div>
                </div>
            </div>

            <div className="card-body row" style={{ flex: "none" }}>
                <div className={`${displayMode === "pipeline" ? "" : "d-none"}`}>
                    {
                        isLoadingDataDetailPipeline ?
                            <div className="loading-overlay">
                                <div className="loader"></div>
                            </div>
                            :
                            Object.keys(customLead).map((key, index) => {
                                return (
                                    <div className="row pb-2 col-12" key={index}>
                                        <div className="col-4 text-end" style={{ fontWeight: "500", color: "#767373" }}>{customLead[key].name}</div>
                                        <div className="col-8" style={{ fontWeight: "500", color: "black" }}>{customLead[key].value}</div>
                                    </div>
                                )
                            })
                    }
                </div>
                <div className={`${displayMode === "log" ? "" : "d-none"}`}>
                    {
                        isLoadingDataLogPipeline ?
                            <div className="loading-overlay">
                                <div className="loader"></div>
                            </div>
                            :
                            log.map((dataLog: Log, index) => {
                                const progressName = dataLog.lead_progress_name;
                                const subProgressName = dataLog.lead_sub_progress_name;
                                const progressColor = dataLog.lead_progress_color;

                                const badgeContent = subProgressName ? `${progressName} (${subProgressName})` : progressName;
                                const badgeStyle = progressColor ? { backgroundColor: progressColor, color: isColorDarkFromHex(progressColor) ? "white" : "black" } : {};

                                var dataDescription = (dataLog.description).replaceAll("\n", "<br/>").trim()

                                return (
                                    <div className="d-print-none border border-dashed border-gray-300 card-rounded p-9 bg-lighten mb-3" key={index}>
                                        <div className="mb-1">
                                            <BadgeTooltip tooltipText={badgeContent} badgeClass={progressColor ? '' : 'badge-light-info'} style={badgeStyle}>
                                                {badgeContent}
                                            </BadgeTooltip>
                                        </div>
                                        <h6 className="mb-8 fw-boldest text-gray-600">{dataLog.creator_name}</h6>
                                        <div className="mb-6">
                                            <div className="fw-bold text-gray-600 fs-7">{intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.DATE_STARTED" })}:</div>
                                            <div className="fw-bolder text-gray-800 fs-6">{moment(dataLog.date_created).format('dddd, DD MMM YYYY HH:mm')}</div>
                                        </div>
                                        <div className="mb-6">
                                            <div className="fw-bold text-gray-600 fs-7">{user.data.company_id === companyId.juraganMaterial ? intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.PREV_PROGRESS_DURATION" }) : intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.DURATION" })}:</div>
                                            <div className="fw-bolder text-gray-800 fs-6">{dataLog.duration}</div>
                                        </div>
                                        <div className={`${hasValidAttachments(dataLog.attachments) ? "mb-6" : ""}`}>
                                            <div className="fw-bold text-gray-600 fs-7">{intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.DESCRIPTION" })}:</div>
                                            <div className="fs-6 d-flex align-items-center">
                                                {dataDescription ? <ExpandableElement text={dataDescription} maxHeight={45} /> : "-"}
                                            </div>
                                        </div>
                                        {
                                            hasValidAttachments(dataLog.attachments) &&
                                            <div>
                                                <div className="fw-bold text-gray-600 fs-7">{intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.ATTACHMENT" })}:</div>
                                                <div className="fw-bolder fs-6 text-gray-800 d-flex align-items-center">
                                                    {
                                                        dataLog.attachments.map((attachment: LogAttachment, index: number) => {
                                                            return (
                                                                <span key={index}>
                                                                    <a href={attachment.url} target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: "FORM.ACTION.VIEW_FILE" })} {index + 1}</a>
                                                                    {index < dataLog.attachments.length - 1 && ', '}
                                                                </span>
                                                            )
                                                        })
                                                    }
                                                </div>
                                            </div>
                                        }
                                    </div>
                                )
                            })
                    }
                </div>
            </div>
        </div>
    )
}

export { ChatDetailPipeline }